import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
  Conversation,
  ConversationStatus,
} from '@stripe-internal/conversations-client';
import {ANIMATION_DURATION, useWidget} from '@stripe-internal/conversations-ui';
import {useMountEffect} from '../../useMountEffect';
import {requestsEmail, requestsRAC} from './conversationUtils';
import {ConfigContext} from '../../Providers/ConfigProvider';
import {useHandleAuthFailure} from '../../Providers/ProfileProvider/HandleAuthFailureApiProvider';
import {useNewDesignsEnabled} from './useNewDesignsEnabled';

export type BottomDrawerType =
  | 'closeConfirmation'
  | 'email'
  | 'requestACall'
  | 'didWeSolve'
  | 'letsChangeThat'
  | 'feedback'
  | 'reAuthentication';

type Props = {
  conversation: Conversation;
};

export const useActiveBottomDrawer = ({conversation}: Props) => {
  const widget = useWidget();
  const {entrypoint} = useContext(ConfigContext);
  const newDesignsEnabled = useNewDesignsEnabled();

  const {isPollingAuth} = useHandleAuthFailure();

  const [activeBottomDrawer, setActiveBottomDrawer] =
    useState<BottomDrawerType | null>(null);
  const [transitioning, setTransitioning] = useState<boolean>(false);
  const [racConfirmed, setRacConfirmed] = useState<boolean>(false);
  const [emailConfirmed, setEmailConfirmed] = useState<boolean>(false);
  const [conversationEnded, setConversationEnded] = useState<boolean>(false);

  const drawerIsActive = !!activeBottomDrawer || transitioning;
  const disableHeader = drawerIsActive && !racConfirmed && !newDesignsEnabled; // allow minimizing widget if user has confirmed RAC

  const handleBottomDrawerChange = useCallback(
    (drawer: BottomDrawerType | null) => {
      if (
        activeBottomDrawer &&
        drawer !== activeBottomDrawer &&
        activeBottomDrawer !== 'reAuthentication'
      ) {
        setTransitioning(true);
      }

      if (activeBottomDrawer !== 'reAuthentication') {
        setActiveBottomDrawer(drawer);
      }

      if (conversation.context.activeBottomDrawer !== drawer) {
        conversation.setContextValue('activeBottomDrawer', drawer);
      }
    },
    [activeBottomDrawer, conversation],
  );

  useMountEffect(() => {
    if (isPollingAuth) {
      setActiveBottomDrawer('reAuthentication');
    } else if (requestsRAC(conversation)) {
      handleBottomDrawerChange('requestACall');
    } else if (requestsEmail(conversation) && entrypoint !== 'dashboard') {
      handleBottomDrawerChange('email');
    } else if (conversation.context.activeBottomDrawer) {
      handleBottomDrawerChange(
        (conversation.context.activeBottomDrawer as BottomDrawerType) || null,
      );
    }

    setRacConfirmed(conversation.context.channel === 'request_a_call');
    setEmailConfirmed(conversation.context.channel === 'email');
  });

  // Delay enabling the widget header while the drawer is transitioning
  useEffect(() => {
    if (transitioning) {
      const delay = ANIMATION_DURATION;
      const timeoutId = setTimeout(() => setTransitioning(false), delay);

      return () => {
        setTransitioning(false);
        clearTimeout(timeoutId);
      };
    }
  }, [transitioning]);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      if (activeBottomDrawer !== 'reAuthentication' && !isPollingAuth) {
        setActiveBottomDrawer(
          (conversation.context.activeBottomDrawer as BottomDrawerType) || null,
        );
      }
      setRacConfirmed(conversation.context.channel === 'request_a_call');
      setEmailConfirmed(conversation.context.channel === 'email');
      setConversationEnded(conversation.status === ConversationStatus.Inactive);
    });
    return () => sub.unsubscribe();
  }, [activeBottomDrawer, conversation, isPollingAuth]);

  // Disable the widget header while certain bottom drawers are open
  useEffect(() => {
    widget.header.setDisabled(disableHeader);
    return () => widget.header.setDisabled(false);
  }, [widget, disableHeader]);

  // Ensure widget is not minimized while header is disabled
  useEffect(() => {
    const checkVisiblity = () => {
      if (widget.visibility() === 'minimized' && disableHeader) {
        widget.open();
      }
    };

    checkVisiblity();
    const sub = widget.visibilityObservable().subscribe(checkVisiblity);

    return () => sub.unsubscribe();
  }, [widget, disableHeader]);

  useEffect(() => {
    if (isPollingAuth) {
      if (activeBottomDrawer !== 'reAuthentication') {
        setTransitioning(true);
      }
      // don't call handleBottomDrawerChange, because we don't want to set this on conversation
      setActiveBottomDrawer('reAuthentication');
    } else {
      const drawer =
        (conversation.context.activeBottomDrawer as BottomDrawerType) || null;
      if (activeBottomDrawer !== drawer) {
        setTransitioning(true);
      }
      setActiveBottomDrawer(drawer);
    }
  }, [
    isPollingAuth,
    conversation.context.activeBottomDrawer,
    activeBottomDrawer,
  ]);

  const showBottomDrawer = useCallback(
    (bottomDrawerType: BottomDrawerType) =>
      // setTimeout ensures the previous drawer has a chance to unmount, which effects the drawer animation
      setTimeout(() => handleBottomDrawerChange(bottomDrawerType), 0),
    [handleBottomDrawerChange],
  );

  const closeBottomDrawer = useCallback(
    (bottomDrawerType: BottomDrawerType) => {
      if (bottomDrawerType === activeBottomDrawer) {
        handleBottomDrawerChange(null);
      }
    },
    [activeBottomDrawer, handleBottomDrawerChange],
  );

  // This is a temporary hack to disable the bottom drawer for RAC when the new top bar UI is shown
  // TODO(MSE-4737): refactor how this works to a better pattern
  const activeBottomDrawerOverride = useMemo(() => {
    if (newDesignsEnabled && conversationEnded) {
      return null;
    }

    if (
      newDesignsEnabled &&
      racConfirmed &&
      activeBottomDrawer === 'requestACall'
    ) {
      return null;
    }

    if (newDesignsEnabled && emailConfirmed && activeBottomDrawer === 'email') {
      return null;
    }

    return activeBottomDrawer;
  }, [
    newDesignsEnabled,
    conversationEnded,
    racConfirmed,
    activeBottomDrawer,
    emailConfirmed,
  ]);

  return {
    activeBottomDrawer: activeBottomDrawerOverride,
    showBottomDrawer,
    closeBottomDrawer,
  };
};
